import React, { useCallback, useState } from "react";
import { api } from "@/lib/api";
import useFetch from "@/lib/useFetch";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import StatusBadge from "@/components/app/StatusBadge";

function ExposuresTable({ rows }) {
  return (
    <table className="w-full min-w-[800px]">
      <thead>
        <tr className="bg-slate-50 border-b border-slate-200">
          <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">Date</th>
          <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">Employee</th>
          <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">Tool</th>
          <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">Vibration</th>
          <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">Trigger</th>
          <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">Points</th>
          <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">Status</th>
          <th className="text-left text-xs uppercase tracking-wider text-slate-500 p-4">Notes</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-b last:border-0" data-testid={`exposure-row-${r.id}`}>
            <td className="p-4 text-sm font-mono text-slate-700">{r.date}</td>
            <td className="p-4 text-sm font-medium text-slate-900">{r.worker_name}</td>
            <td className="p-4 text-sm text-slate-700">
              <span className="font-mono">{r.tool_code}</span> · {r.tool_name}
            </td>
            <td className="p-4 text-sm font-mono text-slate-700">
              {r.vibration_m_s2.toFixed(1)} m/s²
            </td>
            <td className="p-4 text-sm font-mono text-slate-700">
              {r.trigger_minutes.toFixed(1)} min
            </td>
            <td className="p-4 text-sm font-mono font-bold text-slate-900">
              {r.points.toFixed(1)}
            </td>
            <td className="p-4">
              <StatusBadge status={r.status} />
            </td>
            <td className="p-4 text-sm text-slate-600 max-w-xs truncate">
              {r.notes || "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function Exposures() {
  const fetcher = useCallback(
    () => api.get("/exposures?limit=500").then((r) => r.data),
    []
  );
  const { data, loading } = useFetch(fetcher);
  const rows = data || [];
  const [filter, setFilter] = useState("");

  const filtered = rows.filter((r) => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (
      r.worker_name?.toLowerCase().includes(f) ||
      r.tool_code?.toLowerCase().includes(f) ||
      r.tool_name?.toLowerCase().includes(f) ||
      r.date?.includes(f)
    );
  });

  let body;
  if (loading) body = <p className="p-6 text-slate-500">Loading…</p>;
  else if (filtered.length === 0) body = <p className="p-10 text-center text-slate-500">No exposure records.</p>;
  else body = <ExposuresTable rows={filtered} />;

  return (
    <div className="space-y-6" data-testid="exposures-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Records</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mt-1">
            Exposure logs
          </h1>
        </div>
        <Input
          placeholder="Search employee, tool, date…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-xs"
          data-testid="exposures-search"
        />
      </div>

      <Card className="border-slate-200 overflow-x-auto">{body}</Card>
    </div>
  );
}
